import type { RetrieveInviteResponse } from '@frontend/api-types';
import { ManDiscoBall } from '@frontend/assets';
import { match } from '@frontend/duck-tape';
import type { SignUpSearchParams } from '@frontend/member-route-validators';
import { COPY_SIGNUP_HERO_DEFAULT_VALUE, useEffect, useFeatureIsOn, useFeatureValue, useMemo } from '@frontend/react';
import {
  Banner,
  Button,
  ClerkLoading,
  ClerkUnavailable,
  Divider,
  SSOButton,
  ScrollableContentPanel,
  Text,
  Title,
  Wordmark,
  XStack,
  YStack,
  isUserCancellationError,
  showToast,
  useSSOLoadState,
  type OAuthStrategy,
} from '@frontend/web-react';
import { useAnalytics, useAuthentication } from '@frontend/web/hooks';
import { buildSsoCallbackUrl } from '@frontend/web/utils/signup/buildSsoCallbackUrl';
import { Link, useNavigate } from '@tanstack/react-router';

type SignUpLandingProps = {
  disableSignUpActions?: boolean;
  inviteData?: RetrieveInviteResponse;
  inviteErrorMessage?: string;
  referrerName?: string;
  searchParams: SignUpSearchParams;
};

export const SignUpLanding = ({
  disableSignUpActions = false,
  inviteData,
  inviteErrorMessage,
  referrerName,
  searchParams,
}: SignUpLandingProps) => {
  const navigate = useNavigate();
  const ssoLoadState = useSSOLoadState();
  const {
    value: { heading, subheading },
  } = useFeatureValue('copy_signup_hero', COPY_SIGNUP_HERO_DEFAULT_VALUE);
  const { isLoading, setURLParamData, sso } = useAuthentication();
  const { track } = useAnalytics();
  const { enabled: ssoEnabled } = useFeatureIsOn('is_sso_sign_in_enabled');
  const areSignUpActionsDisabled = disableSignUpActions || isLoading || sso.isOAuthRedirecting;
  const isAcceptInviteFlow = Boolean(searchParams.inviteId) || Boolean(inviteData);
  const entryType = isAcceptInviteFlow ? ('accept-invite' as const) : ('signup' as const);
  const inviteId = inviteData?.id ?? searchParams.inviteId;
  const storedURLParamData = useMemo(
    () => ({
      ...(searchParams.description ? { description: searchParams.description } : {}),
      ...(inviteId ? { inviteId } : {}),
      ...(searchParams.promo ? { promo: searchParams.promo } : {}),
      ...(searchParams.referrer ? { referrer: searchParams.referrer } : {}),
      ...(searchParams.utm_campaign ? { utmCampaign: searchParams.utm_campaign } : {}),
      ...(searchParams.utm_content ? { utmContent: searchParams.utm_content } : {}),
      ...(searchParams.utm_medium ? { utmMedium: searchParams.utm_medium } : {}),
      ...(searchParams.utm_source ? { utmSource: searchParams.utm_source } : {}),
      ...(searchParams.utm_term ? { utmTerm: searchParams.utm_term } : {}),
    }),
    [
      inviteId,
      searchParams.description,
      searchParams.promo,
      searchParams.referrer,
      searchParams.utm_campaign,
      searchParams.utm_content,
      searchParams.utm_medium,
      searchParams.utm_source,
      searchParams.utm_term,
    ],
  );

  const isFrictionlessFlow = searchParams.utm_campaign === 'try-first';

  useEffect(() => {
    track({
      data: {
        entryType,
        flowVariant: 'v2',
        hasInviteId: Boolean(searchParams.inviteId || inviteData?.id),
        hasPromoCode: Boolean(searchParams.promo),
        platform: 'web',
      },
      event: 'signupLandingViewed',
      topic: 'auth',
    });

    if (isFrictionlessFlow) {
      track({
        data: {
          hasTaskDescription: Boolean(searchParams.description),
          platform: 'web',
        },
        event: 'experimentLandingPageViewed',
        topic: 'auth',
      });
    }
  }, [
    entryType,
    inviteData?.id,
    isFrictionlessFlow,
    searchParams.description,
    searchParams.inviteId,
    searchParams.promo,
    track,
  ]);

  useEffect(() => {
    setURLParamData(storedURLParamData);
  }, [setURLParamData, storedURLParamData]);

  const handleSignUpWithEmail = () => {
    setURLParamData(storedURLParamData);

    track({
      data: {
        entryType,
        flowVariant: 'v2',
        hasInviteId: Boolean(searchParams.inviteId || inviteData?.id),
        hasPromoCode: Boolean(searchParams.promo),
        method: 'email',
        platform: 'web',
      },
      event: 'signupMethodSelected',
      topic: 'auth',
    });

    const updatedSearchParams: SignUpSearchParams = {
      ...searchParams,
      ...(inviteId && { inviteId }),
    };
    navigate({ search: updatedSearchParams, to: '/sign-up/email' });
  };

  const handleSSOSignUp = async (strategy: OAuthStrategy) => {
    const provider = strategy === 'oauth_apple' ? 'apple' : 'google';
    const method = strategy === 'oauth_apple' ? 'sso_apple' : 'sso_google';
    setURLParamData(storedURLParamData);

    track({
      data: {
        entryType,
        flowVariant: 'v2',
        hasInviteId: Boolean(searchParams.inviteId || inviteData?.id),
        hasPromoCode: Boolean(searchParams.promo),
        method,
        platform: 'web',
      },
      event: 'signupMethodSelected',
      topic: 'auth',
    });
    track({
      data: {
        entryType,
        flowVariant: 'v2',
        platform: 'web',
        provider,
      },
      event: 'ssoStart',
      topic: 'auth',
    });

    const callbackUrl = buildSsoCallbackUrl(window.location.origin, {
      inviteId: storedURLParamData.inviteId,
      promo: storedURLParamData.promo,
      referrer: storedURLParamData.referrer,
    });

    try {
      await sso.startOAuthFlow({
        mode: 'signUp',
        redirectUrl: callbackUrl,
        redirectUrlComplete: callbackUrl,
        strategy,
      });
    } catch (error) {
      if (isUserCancellationError(error)) {
        track({
          data: {
            entryType,
            flowVariant: 'v2',
            platform: 'web',
            provider,
          },
          event: 'ssoCancel',
          topic: 'auth',
        });
        return;
      }
      track({
        data: {
          entryType,
          errorCategory: 'auth',
          errorCode: 'SSO_START_FAILED',
          errorSource: 'ssoStart',
          flowVariant: 'v2',
          platform: 'web',
          provider,
          retriable: true,
        },
        event: 'ssoFail',
        topic: 'auth',
      });
      const providerName = strategy === 'oauth_apple' ? 'Apple' : 'Google';
      showToast({
        title: `Failed to sign in with ${providerName}. Please try again.`,
        variant: 'danger',
      });
    }
  };

  return (
    <XStack className="h-screen">
      <YStack
        className="hidden flex-1 items-center justify-center bg-cover bg-center bg-no-repeat p-xl md:flex relative"
        style={{ backgroundImage: `url(${ManDiscoBall})` }}
      >
        <div className="size-full absolute bg-white/50"></div>
        <YStack className="z-20">
          <Title className="max-w-md text-center" type="h1">
            {heading}
          </Title>
          {subheading ? (
            <Text className="mt-md max-w-md text-center" type="p-lg">
              {subheading}
            </Text>
          ) : null}
        </YStack>
      </YStack>

      <ScrollableContentPanel containerClassName="bg-cream-100" contentWidth="w-full" justifyContent="center">
        <div className="max-w-lg mx-auto w-full">
          <YStack alignItems="center" className="w-full" gapY="xl">
            <Wordmark className="fill-neutral-950" height={24} width={100} />

            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <YStack alignItems="center" bg="white" className="w-full" gapY="md" p="xl" radius="xl">
              {match(ssoLoadState)
                .with({ status: 'loading' }, () => <ClerkLoading />)
                .with({ status: 'failure' }, ({ onReload }) => <ClerkUnavailable onAction={onReload} />)
                .with({ status: 'success' }, () => (
                  <>
                    <Title textAlign="center" type="h5">
                      {isAcceptInviteFlow ? 'Accept your invite' : 'Create your account'}
                    </Title>

                    {referrerName ? (
                      <Text color="secondary" textAlign="center" type="p-sm">
                        {referrerName} wants to share Duckbill with you
                      </Text>
                    ) : null}

                    {inviteErrorMessage ? (
                      <Banner color="danger" leftIconName="IconAlertCircle" title={inviteErrorMessage} />
                    ) : null}

                    <YStack className="w-full" gapY="md">
                      {ssoEnabled ? (
                        <>
                          <SSOButton
                            disabled={areSignUpActionsDisabled || !sso.isLoaded}
                            isLoading={isLoading || sso.activeProvider === 'oauth_google'}
                            label="Sign up with Google"
                            onClick={() => handleSSOSignUp('oauth_google')}
                            provider="google"
                          />
                          <SSOButton
                            disabled={areSignUpActionsDisabled || !sso.isLoaded}
                            isLoading={isLoading || sso.activeProvider === 'oauth_apple'}
                            label="Sign up with Apple"
                            onClick={() => handleSSOSignUp('oauth_apple')}
                            provider="apple"
                          />
                          <Divider label="or" labelPosition="center" />
                        </>
                      ) : null}

                      <Button
                        disabled={areSignUpActionsDisabled}
                        fullWidth
                        onClick={handleSignUpWithEmail}
                        variant="secondary"
                      >
                        Sign up with Email
                      </Button>
                    </YStack>
                  </>
                ))
                .exhaustive()}
            </YStack>
            <XStack gapX="xs">
              <Text type="p-sm">Already have an account?</Text>
              <Link className="underline underline-offset-4" to="/login">
                <Text type="p-sm">Sign in</Text>
              </Link>
            </XStack>
          </YStack>
        </div>
      </ScrollableContentPanel>
    </XStack>
  );
};
